import React, { useEffect, useState, useMemo, useCallback } from "react";
import { 
  Box, 
  Flex, 
  Grid, 
  GridItem, 
  Heading, 
  Text, 
  Divider,
  Alert,
  AlertIcon, 
  AlertTitle, 
  AlertDescription,
  HStack,
  Button,
  Select,
  IconButton,
  useColorModeValue
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import MeetingHeader from "./MeetingHeader";
import MeetingCard from "./MeetingCard";
import MeetingCardSkeleton from "./MeetingCardSkeleton";
import { useMeetingContext } from "./MeetingContext";
import type { Meeting as ApiMeeting } from "../../services/meetingService";
import { MeetingContentProps, PendingActionItem } from "./types";

const PAGINATION_STORAGE_KEY = "meetingPaginationState";

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
}

const MeetingContent: React.FC<MeetingContentProps> = () => {
  const { 
    filteredMeetings, 
    isLoading, 
    error, 
    meetings: allMeetings,
    fetchMeetings,
    pagination: contextPagination,
    searchTerm,
    isSearching,
    hasApiSearchResults
  } = useMeetingContext();
  
  const [showContent, setShowContent] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });

  // Color mode values
  const paginationBg = useColorModeValue("white", "gray.800");
  const buttonHoverBg = useColorModeValue("gray.50", "gray.700");

  // Get user from localStorage for role-based filtering
  const user = useMemo(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        console.error("Failed to parse user from localStorage", e);
        return null;
      }
    }
    return null;
  }, []);

  // Calculate meetings with conversation data for accurate result counting
  const meetingsWithConversationData = useMemo(() => {
    const result: { [key: string]: ApiMeeting[] } = {};
    let totalCount = 0;
    
    // Check if user has Sales role (and not admin)
    const isSalesRole = user && user.roles && user.roles.includes("Sales");
    const userId = user?.user_id;
    
    Object.entries(filteredMeetings).forEach(([group, meetingsInGroup]) => {
      let validMeetings = meetingsInGroup.filter(meeting => {
        if (!meeting.conversation_id) return false;
        
        // Handle array case
        if (Array.isArray(meeting.conversation_id)) {
          return meeting.conversation_id.length > 0 && 
                 meeting.conversation_id.some(id => typeof id === 'string' && id.trim() !== '');
        }
        
        // Handle string case
        if (typeof meeting.conversation_id === 'string') {
          return meeting.conversation_id.trim() !== '';
        }
        
        return false;
      });
      
      // If user is Sales (not admin), filter by salesExecutiveId
      // if (isSalesRole && userId && !user.is_admin) {      
      if (isSalesRole && userId) {
        validMeetings = validMeetings.filter(meeting => 
          meeting.salesExecutiveId && String(meeting.salesExecutiveId) === String(userId)
        );
      }
      
      if (validMeetings.length > 0) {
        result[group] = validMeetings;
        totalCount += validMeetings.length;
      }
    });
    
    return { groups: result, totalCount };
  }, [filteredMeetings, user]);

  const hasValidResults = meetingsWithConversationData.totalCount > 0;
  const noFilteredResults = !hasValidResults && (Object.keys(filteredMeetings).length > 0 || allMeetings.length > 0);  
  // Update local pagination state when context pagination changes
  useEffect(() => {
    if (contextPagination) {
      setPagination({
        currentPage: contextPagination.currentPage,
        totalPages: contextPagination.totalPages,
        totalItems: contextPagination.totalItems,
        limit: contextPagination.limit
      });

      // Save to localStorage only when not searching
      if (typeof window !== "undefined" && !searchTerm.trim()) {
        localStorage.setItem(
          PAGINATION_STORAGE_KEY,
          JSON.stringify({
            page: contextPagination.currentPage,
            limit: contextPagination.limit,
          })
        );
      }
    }
  }, [contextPagination, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoading) {
        setShowContent(true);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Fetch meetings with pagination
  const fetchMeetingsWithPagination = useCallback(
    async (page: number = 1, limit: number = 10) => {
      try {
        const params: any = {
          page,
          limit,
          type: "real_estate",
          sortBy: "startTime",
          sortOrder: "desc",
        };

        // Include search term if it exists
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
          params.limit = 50; // Increase limit when searching to get more results
          // Clear pagination from localStorage when searching
          if (typeof window !== "undefined") {
            localStorage.removeItem(PAGINATION_STORAGE_KEY);
          }
        } else {
          // Save pagination to localStorage when not searching
          if (typeof window !== "undefined") {
            localStorage.setItem(
              PAGINATION_STORAGE_KEY,
              JSON.stringify({ page, limit })
            );
          }
        }

        await fetchMeetings(params);
      } catch (error) {
        console.error("Error fetching paginated meetings:", error);
      }
    },
    [fetchMeetings, searchTerm]
  );

  // Handle page change
  const handlePageChange = async (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.currentPage) {
      await fetchMeetingsWithPagination(newPage, pagination.limit);
    }
  };

  // Handle items per page change
  const handleLimitChange = async (newLimit: number) => {
    await fetchMeetingsWithPagination(1, newLimit);
  };

  // Initial load is handled by MeetingContext

  const getPendingActionsForMeeting = (meetingId: number | string): { count: number; items: PendingActionItem[] } => {
    const numericId = typeof meetingId === 'string' ? parseInt(meetingId, 10) : meetingId;
    
    const defaultActions = { count: 0, items: [] };

    if (isNaN(numericId)) return defaultActions;

    // Mock data - values are string descriptions
    const pendingActionsDataMap: { [key: number]: { descriptions: string[] } } = {
      1: { descriptions: ["Send Visuals to Client", "Send Cost Sheet to Client"] },
      2: { descriptions: ["Send Visuals to Client", "Send Cost Sheet to Client"] },
      3: { descriptions: ["Send Visuals to Client", "Send Cost Sheet to Client"] }, // Assuming 2 items based on pattern, original had trailing comma
      4: { descriptions: ["Send Visuals to Client", "Send Cost Sheet to Client"] },
      5: { descriptions: ["Close Client", "Clear Questions"] },
      6: { descriptions: ["Send Visuals to Client", "Send Cost Sheet to Client"] },
    };

    const data = pendingActionsDataMap[numericId as keyof typeof pendingActionsDataMap];

    if (!data) {
      return defaultActions;
    }

    const items: PendingActionItem[] = data.descriptions.map((desc, index) => ({
      id: `${numericId}-action-${index + 1}`, // Generate a unique ID
      description: desc,
      // isCompleted and dueDate can be omitted or set to default values if needed
    }));

    return {
      count: items.length,
      items: items,
    };
  };

  const getDisplayDuration = (meeting: ApiMeeting, conversationId?: string) => {
    // Check if conversationDurations exists and has duration for this specific conversation
    if (conversationId && meeting.conversationDurations) {
      const conversationDuration = meeting.conversationDurations[conversationId];
      if (conversationDuration && conversationDuration.duration) {
        return conversationDuration.duration;
      }
    }
    // Fall back to meeting duration
    return meeting.duration || "N/A"; 
  };

  const getAnimationDelay = (groupIndex: number, meetingIndex: number) => {
    return 0.1 + (groupIndex * 0.05) + (meetingIndex * 0.1);
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    
    let startPage = Math.max(1, pagination.currentPage - halfRange);
    let endPage = Math.min(pagination.totalPages, pagination.currentPage + halfRange);
    
    // Adjust if we're near the beginning or end
    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === pagination.totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  // Pagination Controls Component
  const PaginationControls = () => (
    <Box 
      bg={paginationBg} 
      p={4} 
      borderRadius="md" 
      borderWidth="1px" 
      borderColor="gray.200"
      boxShadow="sm"
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
        {/* Items per page selector */}
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">Show:</Text>
          <Select 
            size="sm" 
            value={pagination.limit} 
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            width="80px"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </Select>
          <Text fontSize="sm" color="gray.600">per page</Text>
        </HStack>

        {/* Page info */}
        <Text fontSize="sm" color="gray.600">
          Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{" "}
          {Math.min(pagination.currentPage * pagination.limit, pagination.totalItems)} of{" "}
          {pagination.totalItems} meetings 
          {searchTerm && searchTerm.trim() ? ` (search results for "${searchTerm}")` : "(filtered to show only those with conversation data)"}
        </Text>

        {/* Page navigation */}
        <HStack spacing={1}>
          <IconButton
            aria-label="Previous page"
            icon={<ChevronLeftIcon />}
            size="sm"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            isDisabled={pagination.currentPage === 1}
            _hover={{ bg: buttonHoverBg }}
          />
          
          {generatePageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              size="sm"
              variant={pageNum === pagination.currentPage ? "solid" : "ghost"}
              colorScheme={pageNum === pagination.currentPage ? "blue" : "gray"}
              onClick={() => handlePageChange(pageNum)}
              _hover={{ bg: buttonHoverBg }}
            >
              {pageNum}
            </Button>
          ))}
          
          <IconButton
            aria-label="Next page"
            icon={<ChevronRightIcon />}
            size="sm"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            isDisabled={pagination.currentPage === pagination.totalPages}
            _hover={{ bg: buttonHoverBg }}
          />
        </HStack>
      </Flex>
    </Box>
  );

  // if (isLoading) {
  //   return (
  //     <Flex justify="center" align="center" h="100%" p={6}>
  //       <Spinner size="xl" />
  //     </Flex>
  //   );
  // }

  if (error) {
    return (
      <Flex direction="column" p={6} h="100%">
        <MeetingHeader /> 
        <Alert status="error" mt={6}>
          <AlertIcon />
          <AlertTitle mr={2}>Error loading meetings!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Flex>
    );
  }

  // Only show "No Meetings Found" if we're not loading AND we have actually tried to fetch data
  // (pagination exists) AND there are no items  
  if (!isLoading && !error && pagination.totalItems === 0) {
    return (
      <Flex direction="column" p={6} h="100%">
        <MeetingHeader />
        <Flex justify="center" align="center" flexDir="column" textAlign="center" h="80%" mt={6}>
            <Heading as="h3" size="lg" mb={2}>No Meetings Found</Heading>
            <Text color="gray.500">There are currently no meetings to display.</Text>
        </Flex>
      </Flex>
    );
  }
  
  if (!showContent && allMeetings.length > 0) {
    return <Box flex="1" />;
  }

  return (
    <Flex 
      direction="column" 
      p={6} 
      h="100%" 
      overflow="hidden"
    >
      <MeetingHeader />
      
      {/* Search Results Indicator - Only show if there are actual valid results */}
      {!isLoading && !isSearching && searchTerm.trim() && hasValidResults && (
        <Box mt={6} mb={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
          <Text fontSize="sm" color="blue.700">
            Showing search results for "{searchTerm}" • Found {meetingsWithConversationData.totalCount} meetings with conversation data
          </Text>
        </Box>
      )}

      {/* Pagination Controls - Top - Hide during search or when using search results */}
      {/* {!isLoading && !isSearching && !hasApiSearchResults && !searchTerm && (
        <Box mt={6} mb={4}>
          <PaginationControls />
        </Box>
      )} */}

      <Box
        flex="1"
        overflowY="auto"
        px={2}
      >
        {/* Show skeleton cards during initial loading, refetching, or when searching via API */}
        {isLoading || isSearching ? (
          <Grid templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }} gap={6}>
            {Array.from({ length: Math.min(pagination.limit || 6, 6) }).map((_, index) => (
              <GridItem key={index}>
                <MeetingCardSkeleton />
              </GridItem>
            ))}
          </Grid>
        ) : null}
        
        {/* Show no results message - Updated logic to check for actual displayable results */}
        {!isLoading && !isSearching && noFilteredResults ? (
          <Flex justify="center" align="center" flexDir="column" textAlign="center" h="80%">
            <Heading as="h3" size="md" mb={2}>
              {searchTerm.trim() ? 
                `No meetings found for "${searchTerm}"` : 
                "No meetings with conversation data match your current filter."
              }
            </Heading>
            <Text color="gray.500">
              {searchTerm.trim() ? 
                "Try adjusting your search term or check spelling." :
                "Try adjusting your search or filter criteria, or check that meetings have conversation IDs."
              }
            </Text>
          </Flex>
        ) : !isSearching && !isLoading && hasValidResults ? (
          (() => {
            // Track conversation IDs that have been displayed to avoid duplicates across groups
            const displayedConversationIds = new Set<string>();
            let globalCardIndex = 0;
            
            return Object.entries(meetingsWithConversationData.groups).map(([group, meetingsInGroup], groupIndex) => (
              <Box key={group} mb={8}>
                {/* <Heading as="h2" fontSize="lg" fontWeight="semibold" color="gray.700" mb={4}>
                  {group} ({meetingsInGroup.length} meetings with conversation data)
                </Heading> */}
                <Grid 
                  templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }} 
                  gap={6}
                >
                  {meetingsInGroup.flatMap((meeting, meetingIndex) => {
                    // Expand meetings with array conversation_ids into multiple cards
                    let conversationIds = Array.isArray(meeting.conversation_id)
                      ? meeting.conversation_id.filter(id => id && typeof id === 'string' && id.trim() !== '')
                      : (meeting.conversation_id && typeof meeting.conversation_id === 'string' && meeting.conversation_id.trim() !== '')
                        ? [meeting.conversation_id]
                        : [];

                    if (conversationIds.length === 0) {
                      return null;
                    }

                    // Reverse array so latest (last index) comes first
                    conversationIds = [...conversationIds].reverse();

                    // Filter out conversation IDs that have already been displayed
                    const uniqueConversationIds = conversationIds.filter(convId => {
                      if (displayedConversationIds.has(convId)) {
                        return false; // Skip this conversation_id, already displayed
                      }
                      displayedConversationIds.add(convId); // Mark as displayed
                      return true;
                    });

                    if (uniqueConversationIds.length === 0) {
                      return null; // All conversation IDs for this meeting were already displayed
                    }

                    // Create a card for each unique conversation_id
                    return uniqueConversationIds.map((convId, convIndex) => {
                      // Use "Sales Representative || Client name" format (same for all cards)
                      const displayName = `${meeting.salesExecutive} || ${meeting.client}`;
                      
                      const uniqueKey = `${meeting.id}-${convId}-${globalCardIndex}`;
                      const cardIndex = globalCardIndex++;
                      
                      return (
                        <GridItem key={uniqueKey}>
                          <MeetingCard
                            id={`${meeting.id}-${convIndex}`}
                            meeting_id={meeting.meeting_id}
                            conversation_id={convId}
                            name={displayName}
                            date={meeting.date}
                            project={meeting.project}
                            client={meeting.client}
                            clientId={meeting.clientId}
                            salesRep={meeting.salesExecutive}
                            salesExecutiveId={meeting.salesExecutiveId}
                            duration={getDisplayDuration(meeting, convId)}
                            pendingActions={getPendingActionsForMeeting(meeting.id)}
                            delay={getAnimationDelay(groupIndex, cardIndex)}
                          />
                          {cardIndex < (meetingsInGroup.length * 100) - 1 && (cardIndex + 1) % 2 === 0 && (
                            <Divider my={4} borderColor="gray.200" />
                          )}
                        </GridItem>
                      );
                    });
                  }).filter(Boolean)}
                </Grid>
              
              {groupIndex < Object.entries(meetingsWithConversationData.groups).length - 1 && (
                <Divider my={8} borderColor="gray.300" />
              )}
            </Box>
            ));
          })()
        ) : null}
      </Box>

      {/* Pagination Controls - Bottom - Hide during search or when using search results */}
      {!isLoading && !isSearching && pagination.totalItems > 0 && !hasApiSearchResults && hasValidResults && (
        <Box mt={4}>
          <PaginationControls />
        </Box>
      )}
    </Flex>
  );
};

export default MeetingContent;
